// WordPress-compatible slug generation - Exact replica of WordPress implementation

/**
 * WordPress fs_sanitize_slug function implementation
 * From flatsome-child/inc/admin-page.php lines 481-491
 */
export function wordpressSanitizeSlug(str: string): string {
  if (!str) return ''

  // Convert to lowercase
  let sanitized = str.toLowerCase()

  // Remove accents and special characters (Vietnamese, Spanish, Portuguese, French, etc.)
  sanitized = sanitized
    // Vietnamese accents
    .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
    .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
    .replace(/[ìíịỉĩ]/g, 'i')
    .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
    .replace(/[ùúụủũưừứựửữ]/g, 'u')
    .replace(/[ỳýỵỷỹ]/g, 'y')
    .replace(/đ/g, 'd')
    .replace(/[ÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴ]/g, 'a')
    .replace(/[ÈÉẸẺẼÊỀẾỆỂỄ]/g, 'e')
    .replace(/[ÌÍỊỈĨ]/g, 'i')
    .replace(/[ÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠ]/g, 'o')
    .replace(/[ÙÚỤỦŨƯỪỨỰỬỮ]/g, 'u')
    .replace(/[ỲÝỴỶỸ]/g, 'y')
    .replace(/Đ/g, 'd')
    // Spanish, Portuguese, French accents
    .replace(/[áàâäãåæ]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[íìîï]/g, 'i')
    .replace(/[óòôöõø]/g, 'o')
    .replace(/[úùûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[ÁÀÂÄÃÅÆ]/g, 'a')
    .replace(/[ÉÈÊË]/g, 'e')
    .replace(/[ÍÌÎÏ]/g, 'i')
    .replace(/[ÓÒÔÖÕØ]/g, 'o')
    .replace(/[ÚÙÛÜ]/g, 'u')
    .replace(/[Ñ]/g, 'n')
    .replace(/[Ç]/g, 'c')

  // Remove non-alphanumeric characters except spaces and hyphens
  sanitized = sanitized.replace(/[^a-z0-9\s-]/g, '')

  // Replace spaces and underscores with hyphens
  sanitized = sanitized.replace(/[\s_]+/g, '-')

  // Trim hyphens from start and end
  sanitized = sanitized.replace(/^-+|-+$/g, '')

  return sanitized
}

/**
 * WordPress generate_seo_slug equivalent
 * Based on usage patterns in load-matches.php and home-matches.php
 */
export function generateSeoSlug(name: string, fallback: string = ''): string {
  // Type safety check
  if (typeof name !== 'string') {
    return fallback || 'unknown'
  }
  
  if (!name || name.trim() === '') {
    return fallback || 'unknown'
  }
  return wordpressSanitizeSlug(name)
}

/**
 * WordPress-style match URL generation
 * Format: /truc-tiep/{team_home_slug}-vs-{team_away_slug}-{league_slug}-{dd-mm-yyyy}
 * Example: paok-saloniki-vs-rijeka-uefa-europa-league-29-08-2025
 */
export function generateWordPressMatchUrl(
  teamHome: string,
  teamAway: string,
  league?: string,
  matchDate?: string | Date,
  duplicateIndex?: number
): string {
  // Generate team slugs (use provided slug or generate from name)
  const teamHomeSlug = generateSeoSlug(teamHome, 'team-home')
  const teamAwaySlug = generateSeoSlug(teamAway, 'team-away')
  const leagueSlug = generateSeoSlug(league || '', 'giai-dau')

  // Format date as dd-mm-yyyy (WordPress format)
  let dateSlug = ''
  if (matchDate) {
    const date = typeof matchDate === 'string' ? new Date(matchDate) : matchDate
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      dateSlug = `${day}-${month}-${year}`
    }
  }

  // WordPress format: team-home-vs-team-away-league-dd-mm-yyyy
  const slugParts = [teamHomeSlug, 'vs', teamAwaySlug]
  if (leagueSlug) slugParts.push(leagueSlug)
  if (dateSlug) slugParts.push(dateSlug)
  
  let slug = slugParts.join('-')
  
  // Add duplicate index if needed (for duplicate matches on same day)
  if (duplicateIndex && duplicateIndex > 0) {
    slug += `-${duplicateIndex}`
  }

  // WordPress URL structure
  return `/truc-tiep/${slug}`
}

/**
 * Parse WordPress-style slug back to components
 * Input: "paok-saloniki-vs-rijeka-uefa-europa-league-29-08-2025" or "...29-08-2025-1"
 * Output: { teamHome: "paok-saloniki", teamAway: "rijeka", league: "uefa-europa-league", date: "29-08-2025", duplicateIndex: null }
 */
export function parseWordPressSlug(slug: string): {
  teamHome: string;
  teamAway: string; 
  league: string;
  date: string | null;
  duplicateIndex: number | null;
} {
  const decodedSlug = decodeURIComponent(slug)

  // WordPress pattern: {team-home}-vs-{team-away}-{league}-{dd-mm-yyyy}[-{duplicate-index}]
  
  // Check for duplicate index (single digit at the end)
  const duplicateMatch = decodedSlug.match(/-(\d+)$/)
  const duplicateIndex = duplicateMatch ? parseInt(duplicateMatch[1]) : null
  
  // Remove duplicate index if present
  let slugWithoutDuplicate = decodedSlug
  if (duplicateIndex !== null) {
    slugWithoutDuplicate = decodedSlug.replace(/-\d+$/, '')
  }

  // Check for date format dd-mm-yyyy at the end
  const dateMatch = slugWithoutDuplicate.match(/-(\d{2}-\d{2}-\d{4})$/)
  const date = dateMatch ? dateMatch[1] : null

  // Remove date from slug to parse the rest
  let slugWithoutDate = slugWithoutDuplicate
  if (date) {
    slugWithoutDate = slugWithoutDuplicate.replace(/-\d{2}-\d{2}-\d{4}$/, '')
  }

  // Split by "-vs-" to get team parts
  const parts = slugWithoutDate.split('-vs-')
  if (parts.length < 2) {
    return {
      teamHome: '',
      teamAway: '',
      league: '',
      date,
      duplicateIndex
    }
  }

  const teamHome = parts[0]
  const remaining = parts[1]

  // The remaining part contains: team-away-league
  // Need to identify where team name ends and league begins
  const remainingParts = remaining.split('-')

  if (remainingParts.length === 0) {
    return {
      teamHome,
      teamAway: '',
      league: '',
      date,
      duplicateIndex
    }
  } else if (remainingParts.length === 1) {
    // Only team away, no league
    return {
      teamHome,
      teamAway: remainingParts[0],
      league: '',
      date,
      duplicateIndex
    }
  } else {
    // Assume first part is team away, rest is league
    const teamAway = remainingParts[0]
    const league = remainingParts.slice(1).join('-')

    return {
      teamHome,
      teamAway,
      league,
      date,
      duplicateIndex
    }
  }
}

/**
 * WordPress-style match data extraction
 * WordPress uses post slug to identify matches and extract match details
 */
export function extractWordPressMatchData(slug: string): {
  teamHome: string;
  teamAway: string;
  league: string;
  date: string | null;
  duplicateIndex: number | null;
} {
  return parseWordPressSlug(slug)
}

/**
 * WordPress-style match lookup by teams and date
 * Since WordPress doesn't include match ID in slug, we need to search by team names and date
 */
export function findWordPressMatchBySlug(slug: string): {
  teamHome: string;
  teamAway: string;
  league: string;
  date: string | null;
  duplicateIndex: number | null;
  searchKey: string;
} {
  const parsed = parseWordPressSlug(slug)
  
  // Create a search key for database lookup
  const searchKey = `${parsed.teamHome}_vs_${parsed.teamAway}_${parsed.date || ''}`
  
  return {
    ...parsed,
    searchKey
  }
}

/**
 * Check if a slug follows WordPress format
 * WordPress format: team-home-vs-team-away-league-dd-mm-yyyy (optionally with -N for duplicates)
 */
export function isWordPressSlug(slug: string): boolean {
  const decodedSlug = decodeURIComponent(slug)

  // Must contain "-vs-" and optionally end with date format dd-mm-yyyy
  return decodedSlug.includes('-vs-') && 
         (decodedSlug.match(/-\d{2}-\d{2}-\d{4}(-\d+)?$/) !== null || 
          decodedSlug.split('-vs-').length >= 2)
}

/**
 * Convert current Next.js slug to WordPress format
 * This helps with migration from current format to WordPress format
 */
export function convertToWordPressSlug(
  currentSlug: string,
  teamHome?: string,
  teamAway?: string,
  league?: string,
  matchDate?: string | Date,
  duplicateIndex?: number
): string {
  // If we have explicit team/league/date data, use that
  if (teamHome && teamAway) {
    return generateWordPressMatchUrl(teamHome, teamAway, league, matchDate, duplicateIndex)
  }

  // Otherwise, try to parse current slug and convert
  // This is a best-effort conversion
  const segments = currentSlug.split('-')

  // Look for "vs" to split teams
  const vsIndex = segments.findIndex(segment => segment === 'vs')
  if (vsIndex === -1) {
    // No "vs" found, create generic WordPress slug
    return generateWordPressMatchUrl('team-home', 'team-away', 'giai-dau', matchDate, duplicateIndex)
  }

  const homeTeamParts = segments.slice(0, vsIndex)
  const remainingParts = segments.slice(vsIndex + 1)

  // Extract team away (assume first 1-2 parts after "vs")
  const awayTeamParts = remainingParts.slice(0, 2)
  const leagueParts = remainingParts.slice(2)

  const homeTeam = homeTeamParts.join('-')
  const awayTeam = awayTeamParts.join('-')
  const leagueName = leagueParts.filter(part => 
    !part.match(/^\d/) && // Not a date part
    !part.includes('ngay') && // Not a date part
    !part.includes('luc') && // Not a time part
    !part.includes('blv') // Not a caster part
  ).join('-')

  return generateWordPressMatchUrl(
    homeTeam || 'team-home',
    awayTeam || 'team-away', 
    leagueName || 'giai-dau',
    matchDate,
    duplicateIndex
  )
}

/**
 * Generate WordPress match slug from match data (compatible with WordPress backend)
 * This replicates the exact logic from flatsome-child/inc/admin-page.php
 */
export function generateWordPressSlugFromMatch(match: {
  team_hone?: { name?: string } | string;
  team_away?: { name?: string } | string;
  league?: { name?: string } | string;
  time_start?: string;
}, duplicateIndex?: number): string {
  // Extract team names
  const teamHome = typeof match.team_hone === 'object' 
    ? match.team_hone?.name || '' 
    : match.team_hone || ''
  const teamAway = typeof match.team_away === 'object' 
    ? match.team_away?.name || '' 
    : match.team_away || ''
  const league = typeof match.league === 'object' 
    ? match.league?.name || '' 
    : match.league || ''

  // Format date as dd-mm-yyyy (same as WordPress: date('d-m-Y', strtotime($time_start)))
  let matchDate: Date | undefined
  if (match.time_start) {
    matchDate = new Date(match.time_start)
  }

  return generateWordPressMatchUrl(teamHome, teamAway, league, matchDate, duplicateIndex)
}

/**
 * Generate unique WordPress slug by checking for duplicates
 * If base slug exists, it will try -1, -2, -3 etc until unique slug is found
 */
export function generateUniqueWordPressSlug(
  teamHome: string,
  teamAway: string,
  league?: string,
  matchDate?: string | Date,
  existingSlugs: string[] = []
): string {
  // Generate base slug without duplicate index
  const baseSlug = generateWordPressMatchUrl(teamHome, teamAway, league, matchDate)
  const baseSlugWithoutPrefix = baseSlug.replace(/^\/truc-tiep\//, '')

  // If base slug is unique, return it
  if (!existingSlugs.includes(baseSlugWithoutPrefix)) {
    return baseSlug
  }

  // Try with incremental numbers
  let duplicateIndex = 1
  while (duplicateIndex <= 100) { // Safety limit
    const slugWithIndex = generateWordPressMatchUrl(teamHome, teamAway, league, matchDate, duplicateIndex)
    const slugWithIndexWithoutPrefix = slugWithIndex.replace(/^\/truc-tiep\//, '')
    
    if (!existingSlugs.includes(slugWithIndexWithoutPrefix)) {
      return slugWithIndex
    }
    
    duplicateIndex++
  }

  // Fallback: use timestamp if all numbers are taken
  const timestamp = Date.now()
  return generateWordPressMatchUrl(teamHome, teamAway, league, matchDate, timestamp)
}

/**
 * Get WordPress post name from slug (for WordPress compatibility)
 * WordPress stores slug in post_name field without the /truc-tiep/ prefix
 */
export function getWordPressPostName(fullSlug: string): string {
  // Remove /truc-tiep/ prefix if present
  return fullSlug.replace(/^\/truc-tiep\//, '')
}
